/**
 * Single source of truth for per-person colors.
 *
 * Every surface that shows a user — owner chips, DRI chips, avatars,
 * presence pills, comment authors, action item owners — uses colorFor(name)
 * so the same person is always the same color across the whole dashboard.
 *
 * Customizing a specific person's color:
 *   Edit USER_COLORS below. Anyone not listed falls back to a stable hash of
 *   their name, so new team members get a consistent auto-assigned color too.
 */

export const USER_COLORS = {
  Ricky:     '#3b82f6', // blue
  Zoe:       '#8b5cf6', // purple
  Christina: '#ec4899', // pink
  Katie:     '#f97316', // orange
  Margo:     '#22c55e', // green
  Emily:     '#06b6d4', // cyan
  Sandra:    '#eab308', // yellow
  Sergina:   '#ef4444', // red
  Jonathan: '#14b8a6', // teal
  Chris:     '#a855f7', // violet
  Harold:    '#84cc16', // lime
  Jessie:    '#f43f5e', // rose
  Christine: '#6366f1', // indigo
  Braden:    '#0891b2', // darker cyan
  Colin:     '#d946ef', // fuchsia
  Jane:      '#65a30d', // dark green
}

// Deterministic fallback palette for any name not in USER_COLORS.
// Hash-based so the same unknown name always maps to the same color.
const FALLBACK_PALETTE = [
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899',
]

/**
 * Return the brand color for a given team member name.
 * Falls back to a hash-based color if the name isn't in USER_COLORS.
 */
export function colorFor(name) {
  if (!name || name === '—' || name === 'TBD') return '#9ca3af' // muted gray
  if (USER_COLORS[name]) return USER_COLORS[name]
  // Hash fallback — stable assignment for anyone not in the explicit map
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0
  }
  return FALLBACK_PALETTE[Math.abs(hash) % FALLBACK_PALETTE.length]
}

/**
 * Convenience: given a hex color, return a tinted-background pill style.
 * Used by owner chips, DRI chips, etc.
 *
 * The 18 / 30 / 100 suffixes are percent-opacity in hex (18 ≈ 10%,
 * 30 ≈ 20%, and the bare color = 100%).
 */
export function chipStyle(color) {
  return {
    background: color + '18',
    color: color,
    border: `1px solid ${color}30`,
  }
}
